import type { Drawing, DrawingAnchors, DrawingScope, DrawingStyle, DrawingTool } from './types';
import { getDrawingNoteHtml, noteHtmlToPlainText } from './noteHtml';
import { normalizeTimeSec } from './time';

function toEpochSec(value: unknown): number | null {
  return normalizeTimeSec(value);
}

function normalizeAnchors(tool: DrawingTool, anchors: any): DrawingAnchors {
  if (tool === 'note') {
    return {
      t: toEpochSec(anchors?.t) ?? 0,
      p: Number(anchors?.p ?? 0),
      dx: Number.isFinite(Number(anchors?.dx)) ? Number(anchors?.dx) : 12,
      dy: Number.isFinite(Number(anchors?.dy)) ? Number(anchors?.dy) : -12,
      box: anchors?.box && Number.isFinite(Number(anchors?.box?.w)) && Number.isFinite(Number(anchors?.box?.h))
        ? { w: Number(anchors.box.w), h: Number(anchors.box.h) }
        : undefined,
    };
  }
  if (tool === 'hline') {
    return { p: Number(anchors?.p ?? 0) };
  }
  if (tool === 'vline') {
    return { t: toEpochSec(anchors?.t) ?? 0 };
  }
  if (tool === 'channel') {
    return {
      t1: toEpochSec(anchors?.t1) ?? 0,
      p1: Number(anchors?.p1 ?? 0),
      t2: toEpochSec(anchors?.t2) ?? 0,
      p2: Number(anchors?.p2 ?? 0),
      t3: toEpochSec(anchors?.t3) ?? 0,
      p3: Number(anchors?.p3 ?? 0),
    };
  }
  return {
    t1: toEpochSec(anchors?.t1) ?? 0,
    p1: Number(anchors?.p1 ?? 0),
    t2: toEpochSec(anchors?.t2) ?? 0,
    p2: Number(anchors?.p2 ?? 0),
  } as DrawingAnchors;
}

export function normalizeDrawing(input: Partial<Drawing>): Drawing {
  const tool = String(input.tool || 'trend') as DrawingTool;
  const scope = String(input.scope || 'symbol') as DrawingScope;
  const symbol = String(input.symbol || '').toUpperCase();
  const style: DrawingStyle = {
    lineWidth: Number(input.style?.lineWidth ?? 2),
    lineStyle: input.style?.lineStyle ?? 'solid',
    lineColor: input.style?.lineColor ?? '#3B82F6',
    fillColor: input.style?.fillColor ?? 'rgba(59,130,246,0.18)',
    opacity: Number(input.style?.opacity ?? 1),
    extendLeft: Boolean(input.style?.extendLeft),
    extendRight: Boolean(input.style?.extendRight),
    label: input.style?.label ? String(input.style.label) : undefined,
    fontSize: Number(input.style?.fontSize ?? 14),
    fontColor: input.style?.fontColor ?? '#E5E7EB',
    bgColor: input.style?.bgColor,
    padding: Number(input.style?.padding ?? 8),
  };

  const normalizedNoteHtml = tool === 'note' ? getDrawingNoteHtml({ text: input.text, textHtml: input.textHtml as any }) : undefined;
  const normalizedNoteText = tool === 'note'
    ? (normalizedNoteHtml ? noteHtmlToPlainText(normalizedNoteHtml) : undefined)
    : (typeof input.text === 'string' ? input.text : undefined);

  return {
    id: String(input.id || crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`),
    scope: scope === 'trade' ? 'trade' : 'symbol',
    symbol,
    tradeId: input.tradeId ? String(input.tradeId) : undefined,
    tool,
    anchors: normalizeAnchors(tool, input.anchors || {}),
    text: normalizedNoteText,
    textHtml: normalizedNoteHtml,
    style,
    createdAt: input.createdAt,
    updatedAt: input.updatedAt,
  };
}

export function toApiPayload(drawing: Drawing): any {
  const normalized = normalizeDrawing(drawing);
  return {
    id: normalized.id,
    scope: normalized.scope,
    symbol: normalized.symbol,
    tradeId: normalized.tradeId,
    tool: normalized.tool,
    anchors: normalized.anchors,
    text: normalized.text,
    textHtml: normalized.textHtml,
    style: normalized.style,
  };
}

export function fromApiPayload(input: any): Drawing {
  return normalizeDrawing({
    id: input?.id,
    scope: input?.scope,
    symbol: input?.symbol,
    tradeId: input?.tradeId ?? input?.trade_id,
    tool: input?.tool,
    anchors: input?.anchors ?? input?.anchors_json,
    text: input?.text,
    textHtml: input?.textHtml ?? input?.text_html,
    style: input?.style ?? input?.style_json,
    createdAt: input?.createdAt ?? input?.created_at,
    updatedAt: input?.updatedAt ?? input?.updated_at,
  });
}
